'use client';

import React, { useEffect, useState, ReactNode, ComponentProps } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Image from 'next/image';

interface ModalProps extends Omit<ComponentProps<'div'>, 'children'> {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  closeOnOverlayClick?: boolean;
  showCloseButton?: boolean;
}

const Modal: React.FC<ModalProps> = ({
  children,
  closeOnOverlayClick = true,
  isOpen,
  onClose,
  showCloseButton = true,
  size = 'md',
}) => {
  const [mounted, setMounted] = useState(false);

  // Handle mounting for SSR compatibility
  useEffect(() => {
    setMounted(true);

    // Add event listener to handle Escape key
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);

    // Prevent scrolling on the body when modal is open
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);

  // Handle overlay click
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const isOverlayClick = e.target === e.currentTarget;
    const shouldCloseOnOverlay = closeOnOverlayClick;
    if (isOverlayClick && shouldCloseOnOverlay) {
      onClose();
    }
  };

  // Size classes
  const sizeClasses: Record<string, string> = {
    full: 'max-w-full',
    lg: 'max-w-lg',
    md: 'max-w-md',
    sm: 'max-w-sm',
    xl: 'max-w-xl',
  };

  // Don't render anything on server side
  if (!mounted) return null;

  // Render modal using Portal with framer-motion animations
  return createPortal(
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-9999 flex transform-gpu items-center justify-center bg-black/30 p-4 backdrop-blur-sm"
          onClick={handleOverlayClick}
          aria-modal="true"
          role="dialog"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
        >
          <motion.div
            className={`relative w-full rounded-lg bg-white shadow-xl ${sizeClasses[size] || sizeClasses.md} flex max-h-[90vh] flex-col will-change-transform`}
            onClick={e => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            {/* Header */}
            {showCloseButton && (
              <div className="flex items-center justify-end pt-4 pr-4 pl-4">
                {showCloseButton && (
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                    onClick={onClose}
                    aria-label="Close"
                  >
                    <Image
                      src="/gradient-close-icon.svg"
                      height={45}
                      width={45}
                      alt="Close annoucement"
                    />
                  </button>
                )}
              </div>
            )}

            {/* Body */}
            <div className="flex items-center justify-center overflow-y-auto pt-4 pr-4 pb-12 pl-4">
              {children}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default Modal;
